'use client';

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useCurrentUser, SidebarIcon } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import type { PaddingBox } from '../_shared/resolvePadding';
import { SectionShell } from '../_shared/SectionShell';
import { cardReset } from '../_shared/styles';
import { useMounted } from '../_shared/useMounted';

// ─── BFF response shapes ──────────────────────────────────────────────────────

interface SummaryResponse {
  score?: number;
  totalPoints?: number;
  rank?: number;
  pointName?: string;
  levelName?: string;
  levelCode?: string;
  levelPercent?: number;
  badgeCount?: number;
  streakDays?: number;
  pointsToNextLevel?: number;
  nextLevelName?: string;
  progressPercent?: number;
}

interface BadgeResponse {
  id?: string | number;
  elementId?: string;
  name?: string;
  iconUrl?: string;
  total?: number;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface GamificationPanelProps {
  /** Author-supplied copy overrides (labels / locale). Data always comes from API. */
  streakLabel?: string;
  rankLabel?: string;
  progressTitle?: string;
  pointsRemainingLabel?: string;
  badgesTitle?: string;

  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const ACCENT = 'var(--color-brand-500, #1aa5bc)';
const ACCENT_TINT = 'var(--color-brand-50, rgba(26,165,188,.18))';
const TEXT = 'var(--color-text-primary, #18181b)';
const MUTED = 'var(--color-text-secondary, #71717a)';
const BORDER = 'var(--color-border-default, rgba(0,0,0,0.10))';

const eyebrow: CSSProperties = {
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: 'var(--font-tracking-widest, 0.08em)',
  color: MUTED,
};

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
const bffUrl = (path: string) => `${BASE}${path}`;

// ─── Component ────────────────────────────────────────────────────────────────

export function GamificationPanel({
  streakLabel,
  rankLabel,
  progressTitle,
  pointsRemainingLabel,
  badgesTitle,
  requireAuth,
  paddingOuter,
  paddingInner,
  style,
}: GamificationPanelProps) {
  const { t } = useTranslation('site-builder-blocks');
  const streakText = streakLabel ?? t('gamification.panel.streak');
  const rankText = rankLabel ?? t('gamification.panel.rank');
  const progressText = progressTitle ?? t('gamification.panel.progressTitle');
  const pointsRemainingText = pointsRemainingLabel ?? t('gamification.panel.pointsRemaining');
  const badgesText = badgesTitle ?? t('gamification.panel.badgesTitle');
  const currentUser = useCurrentUser<{
    name?: string;
    surname?: string;
    initials?: string;
    avatar?: string;
  }>();
  const cls = `lms-gamif-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const mounted = useMounted();

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [badges, setBadges] = useState<BadgeResponse[]>([]);
  const [certCount, setCertCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const abort = useRef<AbortController | null>(null);

  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false);
      return;
    }

    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setLoading(true);

    Promise.all([
      fetch(bffUrl('/api/gamification/summary'), { signal: ctrl.signal })
        .then<SummaryResponse | null>((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(bffUrl('/api/gamification/all-badges'), { signal: ctrl.signal })
        .then<BadgeResponse[]>((r) => (r.ok ? r.json() : []))
        .catch(() => []),
      fetch(bffUrl('/api/certificates/issued/search'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstResult: 0, maxResults: 1, filter: {} }),
        signal: ctrl.signal,
      })
        .then<{ totalRecords?: number } | null>((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]).then(([sum, bdg, cert]) => {
      if (ctrl.signal.aborted) return;
      setSummary(sum);
      setBadges(Array.isArray(bdg) ? bdg : []);
      setCertCount(cert?.totalRecords ?? null);
      setLoading(false);
    });

    return () => ctrl.abort();
  }, [isSignedIn]);

  // Hold until mounted so the auth-gated / user-dependent render can't diverge
  // between the server (no redux-persist user) and the first client render.
  if (!mounted) return null;
  if ((gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn)) return null;

  // ── Derived display values ────────────────────────────────────────────────

  const score = summary?.score ?? summary?.totalPoints;
  const levelName = summary?.levelName;
  const bar = clamp(summary?.progressPercent ?? 0);
  const streak = summary?.streakDays ?? 0;
  const rankNum = summary?.rank ?? 0;
  const pointsLeft = summary?.pointsToNextLevel;

  const statTiles = [
    score != null && {
      icon: 'sparkles',
      value: score.toLocaleString(),
      label: summary?.pointName ?? t('gamification.panel.points'),
    },
    summary?.badgeCount != null && {
      icon: 'star',
      value: String(summary.badgeCount),
      label: t('gamification.panel.badge'),
    },
    certCount != null && {
      icon: 'award',
      value: String(certCount),
      label: t('gamification.panel.certificate'),
    },
    rankNum > 0 && { icon: 'trophy', value: `#${rankNum}`, label: rankText },
  ].filter(Boolean) as { icon: string; value: string; label: string }[];

  // earned first, locked last — max 8 shown
  const badgeRow = [...badges].sort((a, b) => (b.total ?? 0) - (a.total ?? 0)).slice(0, 8);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner} style={style}>
      <Card asChild className="sb-app-max-width">
        <section
          style={{
            ...cardReset,
            borderRadius: 'var(--radius-lg, 16px)',
            padding: 'var(--space-6, 24px)',
            maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
            width: '100%',
            margin: '0 auto',
          }}
        >
          <style dangerouslySetInnerHTML={{ __html: css(cls) }} />

          {loading ? (
            <Skeleton cls={cls} />
          ) : (
            <>
              {/* Identity row */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 28,
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* User avatar */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: currentUser?.avatar ? 'transparent' : ACCENT,
                        display: 'grid',
                        placeItems: 'center',
                        border: `2px solid ${ACCENT}`,
                      }}
                    >
                      {currentUser?.avatar ? (
                        <img
                          src={currentUser.avatar}
                          alt={currentUser.name ?? ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <span
                          style={{ color: '#fff', fontWeight: 700, fontSize: 22, lineHeight: 1 }}
                        >
                          {currentUser?.initials ??
                            currentUser?.name?.slice(0, 2).toUpperCase() ??
                            '?'}
                        </span>
                      )}
                    </div>
                    {/* Level badge */}
                    {summary?.levelCode && (
                      <span
                        style={{
                          position: 'absolute',
                          bottom: -4,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: ACCENT,
                          color: '#fff',
                          fontSize: 9.5,
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {summary.levelCode}
                      </span>
                    )}
                  </div>

                  <div>
                    {levelName && <div style={eyebrow}>{levelName}</div>}
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 500,
                        letterSpacing: 'var(--font-tracking-snug, -0.01em)',
                        color: TEXT,
                      }}
                    >
                      {currentUser?.name} {currentUser?.surname}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: MUTED,
                        marginTop: 2,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {streak > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <SidebarIcon name="flame" size={13} />
                          <span>
                            <span style={{ fontWeight: 500, color: TEXT }}>
                              {t('gamification.panel.days', { count: streak })}
                            </span>{' '}
                            {streakText}
                          </span>
                        </span>
                      )}
                      {streak > 0 && rankNum > 0 && (
                        <span style={{ color: BORDER, margin: '0 2px' }}>·</span>
                      )}

                      <span>
                        {rankText} <span style={{ fontWeight: 500, color: TEXT }}>#{rankNum}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stat tiles */}
                {statTiles.length > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${statTiles.length}, minmax(0,1fr))`,
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    {statTiles.map((tile, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 'var(--radius-md, 10px)',
                            display: 'grid',
                            placeItems: 'center',
                            background: ACCENT_TINT,
                            color: ACCENT,
                            flexShrink: 0,
                          }}
                        >
                          <SidebarIcon name={tile.icon} size={18} />
                        </span>
                        <div>
                          <div
                            style={{ fontSize: 18, fontWeight: 600, color: TEXT, lineHeight: 1 }}
                          >
                            {tile.value}
                          </div>
                          <div style={{ ...eyebrow, marginTop: 4 }}>{tile.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div
                style={{
                  height: 1,
                  margin: '20px 0',
                  background: `linear-gradient(90deg,transparent,${BORDER},transparent)`,
                }}
              />

              {/* Progress + badges */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: badgeRow.length > 0 ? 'minmax(0,1fr) auto' : '1fr',
                  gap: 24,
                  alignItems: 'start',
                }}
              >
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}
                  >
                    <div style={eyebrow}>{progressText}</div>
                    {pointsLeft != null && (
                      <div style={{ fontSize: 11.5 }}>
                        <span style={{ fontWeight: 500, color: TEXT }}>
                          {pointsLeft.toLocaleString()}
                        </span>{' '}
                        <span style={{ color: MUTED }}>{pointsRemainingText}</span>
                      </div>
                    )}
                  </div>
                  <div className={`${cls}-bar`}>
                    <i style={{ width: `${bar}%` }} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 8,
                      fontSize: 10.5,
                      color: MUTED,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <SidebarIcon name="trophy" size={12} />
                      {levelName && (
                        <Trans
                          t={t}
                          i18nKey="gamification.panel.currentLevel"
                          values={{ level: levelName }}
                          components={{ b: <span style={{ fontWeight: 500, color: TEXT }} /> }}
                        />
                      )}
                    </span>
                    {summary?.nextLevelName && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Trans
                          t={t}
                          i18nKey="gamification.panel.nextLevel"
                          values={{ level: summary.nextLevelName }}
                          components={{ b: <span style={{ fontWeight: 500, color: TEXT }} /> }}
                        />
                        <SidebarIcon name="chevron-right" size={12} />
                      </span>
                    )}
                  </div>
                </div>

                {badgeRow.length > 0 && (
                  <div>
                    <div style={{ ...eyebrow, marginBottom: 8, textAlign: 'right' }}>
                      {badgesText}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {badgeRow.map((b, i) => {
                        const earned = (b.total ?? 0) > 0;
                        return (
                          <span
                            key={b.id ?? i}
                            className={`${cls}-badge${earned ? '' : ' locked'}`}
                            title={b.name}
                          >
                            {b.iconUrl && <img src={b.iconUrl} alt={b.name ?? ''} />}
                            {(b.total ?? 0) > 1 && <b>{b.total}</b>}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </Card>
    </SectionShell>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function css(cls: string): string {
  return `
.${cls}-ring{position:relative;width:64px;height:64px;border-radius:999px;flex-shrink:0;
  background:conic-gradient(${ACCENT} calc(var(--ringp,0)*1%),${BORDER} 0);display:grid;place-items:center}
.${cls}-ring i{position:absolute;inset:5px;border-radius:999px;background:var(--color-surface-card,#fff);display:grid;place-items:center;
  font-size:15px;font-weight:600;color:${TEXT};font-style:normal}
.${cls}-ring b{position:absolute;bottom:-6px;left:50%;transform:translateX(-50%);background:${ACCENT};color:#fff;
  font-size:9.5px;font-weight:600;padding:1px 6px;border-radius:999px;font-style:normal}
.${cls}-bar{height:8px;border-radius:999px;background:${BORDER};overflow:hidden}
.${cls}-bar>i{display:block;height:100%;border-radius:999px;background:${ACCENT}}
.${cls}-badge{position:relative;width:40px;height:40px;display:inline-grid;place-items:center}
.${cls}-badge img{width:100%;height:100%;object-fit:contain}
.${cls}-badge.locked img{filter:grayscale(1);opacity:.35}
.${cls}-badge b{position:absolute;top:-4px;right:-4px;background:${ACCENT};color:#fff;font-size:9px;font-weight:600;
  min-width:15px;height:15px;padding:0 3px;border-radius:999px;display:grid;place-items:center;font-style:normal}
@keyframes ${cls}-pulse{0%,100%{opacity:1}50%{opacity:.45}}
.${cls}-skel{border-radius:4px;background:var(--color-neutral-100,#f4f4f5);animation:${cls}-pulse 1.5s ease-in-out infinite}
`;
}

function Skeleton({ cls }: { cls: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          className={`${cls}-skel`}
          style={{ width: 64, height: 64, borderRadius: '50%', flexShrink: 0 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          <div className={`${cls}-skel`} style={{ width: '28%', height: 11 }} />
          <div className={`${cls}-skel`} style={{ width: '45%', height: 18 }} />
          <div className={`${cls}-skel`} style={{ width: '22%', height: 11 }} />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3,1fr)',
            gap: 8,
            marginLeft: 'auto',
            width: 280,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${cls}-skel`} style={{ height: 52, borderRadius: 10 }} />
          ))}
        </div>
      </div>
      <div className={`${cls}-skel`} style={{ height: 1 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className={`${cls}-skel`} style={{ width: '20%', height: 10 }} />
        <div className={`${cls}-skel`} style={{ height: 8, borderRadius: 999 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div className={`${cls}-skel`} style={{ width: '18%', height: 10 }} />
          <div className={`${cls}-skel`} style={{ width: '22%', height: 10 }} />
        </div>
      </div>
    </div>
  );
}
